import { useCallback, useEffect, useRef, useState } from 'react';
import { X, PhoneOff, AlignLeft, Info as InfoIcon } from 'lucide-react';
import { useStore } from '../store';
import { salesCrmApi } from '../services/salesCrmApi';
import { leadGenApi, type CallbackPreset } from '../services/leadGenApi';
import { presetToIso, isoToLocalInput, localInputToIso, labelFor, presetFromDate } from '../lib/callbackPresets';
import { buildScriptTokenContext, fillPlaceholders } from '../lib/scriptTokens';
import type { CallOutcome, EmotionTag, LeadStatus, EffectiveScript, Lead, BlockMeta, BlockKind } from '../types';

// -----------------------------------------------------------------------------
// CallCockpit: fokussierter, keyboard-gesteuerter Vollbild-Anruf.
// Ein Script-Abschnitt gross & lesbar im Fokus; per Tasten durch die Sektionen.
// Tracking (Dauer/Outcome/Emotion/Notizen) laeuft im Hintergrund und erst am
// Ende im Abschluss-Panel. Minimale Ablenkung.
// -----------------------------------------------------------------------------

const EMOTIONS: EmotionTag[] = ['warm', 'neutral', 'cold', 'hostile'];
const EMOTION_LABEL: Record<EmotionTag, string> = { warm: 'Warm', neutral: 'Neutral', cold: 'Cold', hostile: 'Hostile' };
const OUTCOMES: CallOutcome[] = ['WON', 'LOST', 'FOLLOW UP', 'NICHT ERREICHT'];
const OUTCOME_LABEL: Record<CallOutcome, string> = {
  WON: 'WON', LOST: 'LOST', 'FOLLOW UP': 'FOLLOW UP', 'NICHT ERREICHT': 'Nicht erreicht',
};
const OUTCOME_HOTKEY: Record<CallOutcome, string> = {
  WON: 'W', LOST: 'L', 'FOLLOW UP': 'F', 'NICHT ERREICHT': 'N',
};
// Termin-Buchung fürs Closing (Calendly). Bei Bedarf später konfigurierbar machen.
// Kontinuierliche Tastatur-Scroll-Geschwindigkeit (px pro Frame ≈ SCROLL_SPEED×60 px/s).
const SCROLL_SPEED = 10;

// Script-Body rendern: Zeilen, die mit „…"/„..." beginnen, sind die erwarteten Lead-Antworten
// (z.B. „… (Hallo?)") und werden grau dargestellt · klar getrennt von dem, was der Setter sagt.
function renderScript(text: string) {
  const lines = (text || '').split('\n');
  return lines.map((line, i) => {
    const isLead = /^\s*(?:…|\.\.\.)/.test(line);
    return (
      <span key={i} className={isLead ? 'text-text-muted' : undefined}>
        {line}{i < lines.length - 1 ? '\n' : ''}
      </span>
    );
  });
}

const CALENDLY_URL = 'https://calendly.com/felix-kellermann-fraud0/30min';
// Synthetische Schluss-Sektion: nach dem Close kommt direkt der eingebettete Calendly-Kalender.
const BOOKING_KEY = '__booking__';
// Unter dieser Dauer fragt der Abschluss nach, ob der Call überhaupt getrackt werden soll (Fehlwahl/kurz).
const SHORT_CALL_SECONDS = 20;
const OUTCOME_KEY: Record<string, CallOutcome> = { w: 'WON', l: 'LOST', f: 'FOLLOW UP', n: 'NICHT ERREICHT' };

function fmt(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

// Die Wiedervorlage-Vorschläge kommen aus den Einstellungen (lead_distribution.callback_presets),
// gerechnet wird in web/src/lib/callbackPresets.ts. Modul-Cache: die Liste ändert sich selten, und
// jeder neue Anruf soll nichts kosten.
let presetCache: CallbackPreset[] | null = null;

// Art eines Blocks: block_meta.kind (aus der Skript-Bibliothek) hat Vorrang; ohne Metadaten (ältere
// Forks, selbst angelegte Blöcke) greift die frühere Heuristik über Key/Titel als Rückfall — so
// bleiben Bestandsskripte ohne Metadaten weiter funktionsfähig.
function kindOf(key: string, sec: { title?: string } | undefined, meta?: BlockMeta): BlockKind {
  if (meta?.kind) return meta.kind;
  const k = (key || '').toLowerCase();
  const t = (sec?.title || '').toLowerCase();
  if (k.startsWith('einwand') || k === 'notfall' || t.startsWith('einwand') || t.startsWith('notfall')) return 'objection';
  return 'flow';
}

// Kurzes Button-Label für eine Abzweigung: „Einwand · Wer bist du?" → „Wer bist du?".
function branchLabel(title: string): string {
  const parts = (title || '').split('·');
  return (parts.length > 1 ? parts.slice(1).join('·') : title).trim() || 'Einwand';
}

// Rückfall für Skripte ohne block_meta.objections (ältere Forks, Bianca-Bestand).
// Unbekannte Sektion → alle vorhandenen Einwände anbieten.
const LEGACY_SECTION_OBJECTIONS: Record<string, string[]> = {
  opener: ['einwand_wer', 'einwand_nummer', 'notfall'],
  verifizierung: ['einwand_ansprechpartner', 'einwand_wer', 'einwand_nummer'],
  hook: ['einwand_funktion', 'notfall'],
  proof: ['einwand_funktion', 'notfall'],
  close: ['einwand_ansprechpartner', 'einwand_funktion'],
};

// Für die aktuelle Sektion die passenden, tatsächlich vorhandenen Einwände (in definierter Reihenfolge).
// Reihenfolge: block_meta.objections der Bibliothek → LEGACY_SECTION_OBJECTIONS → alle Abzweigungen.
function objectionsForSection(sectionKey: string, branchKeys: string[], meta?: Record<string, BlockMeta>): string[] {
  const fromMeta = meta?.[sectionKey]?.objections;
  const mapped = (fromMeta && fromMeta.length ? fromMeta : LEGACY_SECTION_OBJECTIONS[sectionKey]);
  if (!mapped) return branchKeys;
  return mapped.filter((k) => branchKeys.includes(k));
}

export default function CallCockpit() {
  const {
    activeLead, activeVariant, personalizedScript, salesScript,
    callTimerStart, endCall, addCall, updateLead, user,
    linkedinAccounts, selectedLinkedInAccountId,
    calls, fetchCalls, fetchLinkedInAccounts,
  } = useStore();

  // Nachladen, was bisher der Sales-CRM-Mount nebenbei gefüllt hat. Seit das Cockpit app-weit in
  // AppShell.tsx hängt (UMBAU.md Phase 4), kann es auch direkt von der fraud0-Karte aus aufgehen —
  // dann war SalesCrm nie gemountet und diese beiden Listen sind leer: `linkedinAccounts` trägt den
  // Absender im Skript (setterAccount, s.u.), `calls` die letzten Anrufe zu diesem Lead. Nur holen,
  // was fehlt; wer über das Sales CRM kommt, löst hier keinen einzigen Request aus.
  // (Das Skript holt sich das Cockpit ohnehin selbst, siehe getMyFork weiter unten.)
  useEffect(() => {
    if (!linkedinAccounts?.length) fetchLinkedInAccounts().catch(() => {});
    if (!calls?.length) fetchCalls().catch(() => {});
    // Absichtlich nur beim Mount: ein leerer Bestand nach dem Laden ist eine gültige Antwort und
    // darf keine Nachlade-Schleife auslösen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Lokaler Anzeige-Stand des Leads · CallCockpit wird pro Call frisch gemountet
  // ({callViewOpen && <CallCockpit />} in AppShell.tsx), der Initializer greift also je Call neu.
  // Erlaubt, Name/Telefon/E-Mail im Info-Panel zu bearbeiten, ohne auf den store-weiten
  // `activeLead`-Snapshot angewiesen zu sein.
  const [liveLead, setLiveLead] = useState<Lead | null>(activeLead);

  // Info-Panel: eigener Edit-Puffer, beim Öffnen aus liveLead befüllt (siehe useEffect unten).
  const [infoName, setInfoName] = useState('');
  const [infoPhone, setInfoPhone] = useState('');
  const [infoEmail, setInfoEmail] = useState('');
  const [savingInfo, setSavingInfo] = useState(false);
  const [infoError, setInfoError] = useState<string | null>(null);

  // Effektives Skript (Main + eigene Fork-Blöcke) direkt laden · unabhängig davon, WIE der Call
  // gestartet wurde. So steht im Call IMMER das persönliche Skript, nie das Legacy-Markdown.
  // block_meta kommt aus derselben Antwort — die legacy Varianten-Dropdown-Auswahl (activeVariant)
  // kennt keine Metadaten, dort bleibt blockMetaMap einfach leer (Rückfall-Heuristik greift).
  const [fetched, setFetched] = useState<EffectiveScript | null>(null);
  const [fetchedVariantId, setFetchedVariantId] = useState<number | null>(null);
  const [blockMetaMap, setBlockMetaMap] = useState<Record<string, BlockMeta>>({});
  const variantHasSections = !!activeVariant?.section_order?.length;
  const scriptNiche = (activeLead?.niche || '').trim().toLowerCase() || '_global';
  const scriptLang = user?.lang || 'de';
  useEffect(() => {
    if (!activeLead) return;
    if (personalizedScript || variantHasSections) return; // schon ein echtes Skript vorhanden
    let cancelled = false;
    salesCrmApi.getMyFork(scriptNiche, scriptLang)
      .then((res) => {
        if (cancelled) return;
        setFetched(res.script);
        setFetchedVariantId(res.fork?.id ?? res.main?.id ?? null);
        setBlockMetaMap(res.block_meta ?? {});
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [activeLead, personalizedScript, variantHasSections, scriptNiche, scriptLang]);

  // --- Script-Sektionen (personalisiert > aktive Variante > selbst geladenes Fork-Skript > Roh-Script) ---
  const src = personalizedScript
    ?? (variantHasSections
      ? { sections_data: activeVariant!.sections_data, section_order: activeVariant!.section_order, hidden_sections: activeVariant!.hidden_sections ?? [] }
      : null)
    ?? fetched;
  const hasSections = !!src?.section_order?.length;
  // Ausgeblendete Blöcke werden im Call übersprungen · sie bleiben im Skript (Sales-CRM), tauchen hier
  // aber gar nicht erst auf, damit der Setter sie beim Telefonieren nicht durchblättern muss.
  // Entwürfe (block_meta.draft) sind unfertig und werden ebenfalls übersprungen.
  const hiddenSet = new Set(src?.hidden_sections ?? []);
  const order = (hasSections ? src!.section_order : ['_full'])
    .filter((k) => !hiddenSet.has(k))
    .filter((k) => !blockMetaMap[k]?.draft);
  const data = hasSections ? src!.sections_data : { _full: { title: 'Sales Script', body: salesScript || 'Kein Script hinterlegt.' } };
  const effectiveVariantId = activeVariant?.id ?? fetchedVariantId ?? undefined;

  // Auto-Fill-Kontext: [Setter] = ausgewählter LinkedIn-Account (oben rechts); fehlt der, fällt er auf
  // den eingeloggten Nutzernamen zurück (sonst stünde im Live-Skript ein leerer Platzhalter).
  const setterAccount = linkedinAccounts?.find((a) => a.id === selectedLinkedInAccountId);
  const fillCtx = buildScriptTokenContext(
    liveLead ?? activeLead,
    setterAccount?.name || user?.display_name || user?.username,
  );

  // Hauptfluss (lineares Skript) vs. Abzweigungen (Einwände/Notfall · per Button erreichbar).
  // `order` ist bereits hidden-/draft-gefiltert, der Rückfall `order` selbst also ebenfalls.
  // 'note'-Blöcke (Randinfo, kein Sprechtext) tauchen in keiner der beiden Listen auf und
  // damit nie im Call.
  const mainOrder = order.filter((k) => kindOf(k, data[k], blockMetaMap[k]) === 'flow');
  const branchKeys = order.filter((k) => kindOf(k, data[k], blockMetaMap[k]) === 'objection');
  // Hauptfluss + synthetische Buchungs-Sektion am Ende (Calendly).
  const flow = [...(mainOrder.length ? mainOrder : order), BOOKING_KEY];
  const total = flow.length;

  // mainIdx = Position im Hauptfluss; branchKey != null → gerade in einer Abzweigung (Detour).
  const [mainIdx, setMainIdx] = useState(0);
  const [branchKey, setBranchKey] = useState<string | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const [outcome, setOutcome] = useState<CallOutcome | null>(null);
  const [emotions, setEmotions] = useState<EmotionTag[]>([]);
  const [notes, setNotes] = useState('');
  // „Wann wieder anrufen": UTC-ISO mit 'Z'. Der Server schreibt das nach domain_leads.callback_at
  // (daraus sortiert der Telefon-Stapel) und nach leads.follow_up_date.
  const [callbackAt, setCallbackAt] = useState<string | null>(null);
  const [presets, setPresets] = useState<CallbackPreset[]>(presetCache ?? []);
  const [presetIdx, setPresetIdx] = useState<number | null>(null);
  const [exactOpen, setExactOpen] = useState(false);
  const [mode, setMode] = useState<'call' | 'notes' | 'save' | 'confirm' | 'shortcall' | 'info'>('call');
  const [saving, setSaving] = useState(false);
  const notesRef = useRef<HTMLTextAreaElement>(null);

  const idx = Math.min(mainIdx, Math.max(0, total - 1)); // geklemmt (Skript kann async nachladen)
  const onBranch = branchKey != null && !!data[branchKey];
  const curKey = onBranch ? branchKey! : flow[idx];
  const isBooking = !onBranch && curKey === BOOKING_KEY;
  const cur = data[curKey] ?? { title: '', body: '' };
  const curBody = fillPlaceholders(cur.body || '', fillCtx); // Platzhalter live ersetzt
  const nextTitle = !onBranch && idx < total - 1
    ? (flow[idx + 1] === BOOKING_KEY ? 'Termin buchen' : (data[flow[idx + 1]]?.title ?? ''))
    : '';
  // Nur die Einwände, die an der aktuellen Sektion Sinn machen (Position = flow[idx], auch aus einer
  // Abzweigung heraus, damit man von dort zu einem anderen passenden Einwand springen kann).
  const visibleBranches = isBooking ? [] : objectionsForSection(flow[idx], branchKeys, blockMetaMap);
  // Nächste Stufe einer Antwortkette („kein Interesse" Stufe 1 → 2 → 3). Nur gültig, solange der
  // Folgeblock im Skript auch wirklich vorhanden und nicht ausgeblendet/Entwurf ist — sonst führte
  // der Knopf auf einen leeren Bildschirm.
  const nextStageRaw = onBranch ? blockMetaMap[branchKey!]?.next : null;
  const nextStageKey = nextStageRaw && branchKeys.includes(nextStageRaw) ? nextStageRaw : null;
  // Vorzimmer-Schritte darf der Setter überspringen: wer direkt beim Ansprechpartner landet, hat
  // sie nie gebraucht. Ziel ist der erste Schritt, der nicht als optional markiert ist.
  const firstRequiredIdx = flow.findIndex((k) => !blockMetaMap[k]?.optional);
  const canSkipOptional = !onBranch && !isBooking
    && !!blockMetaMap[curKey]?.optional && firstRequiredIdx > idx;

  // Volltext-Modus: ganzes Skript am Stück statt einer Sektion · plus weiches Dauerscrollen per Pfeiltaste
  // (rAF-Loop statt Sprüngen, damit man beim Vorlesen mitkommt).
  const [fullText, setFullText] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const rafRef = useRef<number | null>(null);
  const velRef = useRef(0);
  const stopScroll = useCallback(() => {
    velRef.current = 0;
    if (rafRef.current != null) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
  }, []);
  const startScroll = useCallback((v: number) => {
    velRef.current = v;
    if (rafRef.current != null) return;  // Loop läuft schon · nur Richtung/Tempo aktualisiert
    const step = () => {
      const el = scrollRef.current;
      if (!el || velRef.current === 0) { rafRef.current = null; return; }
      el.scrollTop += velRef.current;    // scrollTop klemmt sich selbst an 0/max → kein Überschiessen
      rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
  }, []);
  useEffect(() => () => { if (rafRef.current != null) cancelAnimationFrame(rafRef.current); }, []);
  const initials = (activeLead?.name || '?')
    .split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase()).join('') || '?';

  // Navigation: weiter/zurück im Hauptfluss; aus einer Abzweigung führt jede Navigation zurück zum
  // Hauptfluss (Detour verlassen, Position bleibt erhalten → „weiter" setzt das Skript fort).
  // Die konkreten Übergänge stehen inline im Keyboard-Handler und an den Buttons (setBranchKey/setMainIdx).

  // Vorschläge einmal holen und den vorgewählten setzen, damit „Niemand rangegangen" ein einziger
  // Tastendruck bleibt. Schlägt das fehl, bleibt callbackAt null — dann stempelt die serverseitige
  // Auto-Kadenz (call_cadence_days). Die Zeitwahl fällt also auf das bisherige Verhalten zurück,
  // sie blockiert nie.
  const applyPresets = useCallback((list: CallbackPreset[]) => {
    setPresets(list);
    const i = list.findIndex((p) => p.default);
    if (i >= 0) { setPresetIdx(i); setCallbackAt(presetToIso(list[i])); }
  }, []);
  useEffect(() => {
    if (presetCache) { applyPresets(presetCache); return; }
    let abgebrochen = false;
    leadGenApi.getCallbackPresets()
      .then((r) => { if (abgebrochen) return; presetCache = r.presets || []; applyPresets(presetCache); })
      .catch(() => { /* ohne Vorschläge übernimmt die Auto-Kadenz */ });
    return () => { abgebrochen = true; };
  }, [applyPresets]);

  // Nochmal antippen wählt ab: dann greift wieder die Auto-Kadenz aus den Einstellungen.
  const waehlePreset = useCallback((i: number) => {
    const ab = presetIdx === i;
    setPresetIdx(ab ? null : i);
    setCallbackAt(ab || !presets[i] ? null : presetToIso(presets[i]));
    setExactOpen(false);
  }, [presetIdx, presets]);

  const waehleGenau = useCallback((iso: string | null) => {
    setPresetIdx(null);
    setCallbackAt(iso);
  }, []);

  // „+ speichern": den frei gewählten Abstand als Vorschlag anhängen. Die Liste ist geteilt, der
  // neue Eintrag steht danach allen zur Wahl. Scheitert das, bleibt die Zeitwahl davon unberührt.
  const merkeAlsVorschlag = useCallback(async () => {
    if (!callbackAt) return;
    try {
      const r = await leadGenApi.addCallbackPreset(presetFromDate(new Date(callbackAt)));
      presetCache = r.presets || [];
      setPresets(presetCache);
    } catch { /* der gewählte Zeitpunkt gilt trotzdem */ }
  }, [callbackAt]);

  // --- Live-Timer ---
  useEffect(() => {
    if (!callTimerStart) return;
    const t = setInterval(() => setElapsed(Math.floor((Date.now() - callTimerStart) / 1000)), 1000);
    return () => clearInterval(t);
  }, [callTimerStart]);

  const toggleEmotion = useCallback((e: EmotionTag) => {
    setEmotions((prev) => (prev.includes(e) ? prev.filter((x) => x !== e) : [...prev, e]));
  }, []);

  const save = useCallback(async (outcomeOverride?: CallOutcome) => {
    if (!activeLead || saving) return;
    // Guard: bei onClick={save} kommt ein Event rein · nur echte Outcome-Strings zählen als Override.
    const effOutcome = (typeof outcomeOverride === 'string' ? outcomeOverride : null) ?? outcome;
    setSaving(true);
    try {
      // Lokaler Zeitstempel ('YYYY-MM-DD HH:MM:SS') → Wochentag/Uhrzeit-Analytik nutzt die Caller-Zeitzone.
      const now = new Date();
      const p = (n: number) => String(n).padStart(2, '0');
      const callTime = `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())} ${p(now.getHours())}:${p(now.getMinutes())}:${p(now.getSeconds())}`;
      // Bei WON/LOST ist der Picker verborgen — der vorgewählte Vorschlag steht aber trotzdem im
      // Zustand und würde sonst einen Rückruf auf einen abgeschlossenen Lead stempeln.
      const effCallback = (effOutcome === 'WON' || effOutcome === 'LOST') ? undefined : (callbackAt ?? undefined);
      const suggested = await addCall({
        lead_id: activeLead.id,
        length_seconds: elapsed || undefined,
        outcome: effOutcome ?? undefined,
        emotions,
        notes: notes || undefined,
        call_time: callTime,
        callback_at: effCallback,
        script_variant_id: effectiveVariantId,
        call_script_sections: hasSections ? src!.sections_data : undefined,
        call_script_section_order: hasSections ? src!.section_order : undefined,
        personalization_context: personalizedScript ? { personalized: true } : undefined,
      });
      const patch: Partial<Lead> = {};
      if (suggested) patch.status = suggested as LeadStatus;
      // follow_up_date wird NICHT mehr von hier gesetzt: der Server schreibt es aus callback_at,
      // zusammen mit domain_leads.callback_at. Zwei Schreiber auf einer Spalte wären ein verlorenes
      // Update gegen die fraud0-Brücke.
      if (Object.keys(patch).length) {
        await updateLead(activeLead.id, patch);
      }
    } finally {
      setSaving(false);
      endCall();
    }
  }, [activeLead, saving, addCall, elapsed, outcome, emotions, notes, callbackAt, effectiveVariantId, hasSections, src, personalizedScript, updateLead, endCall]);

  // Abschluss öffnen: bei sehr kurzem Call erst fragen, ob überhaupt getrackt werden soll.
  const goToSave = useCallback(() => {
    setMode(elapsed < SHORT_CALL_SECONDS ? 'shortcall' : 'save');
  }, [elapsed]);
  // „Nicht rangegangen" · sofort als Nicht-erreicht speichern und beenden.
  const quickNoAnswer = useCallback(() => { save('NICHT ERREICHT' as CallOutcome); }, [save]);

  // Info-Panel öffnet mit dem aktuellen Kontaktstand · frischer Puffer bei jedem Öffnen.
  useEffect(() => {
    if (mode !== 'info') return;
    setInfoName(liveLead?.name ?? '');
    setInfoPhone(liveLead?.phone ?? '');
    setInfoEmail(liveLead?.email ?? '');
    setInfoError(null);
  }, [mode, liveLead]);

  const saveInfo = useCallback(async () => {
    if (!activeLead) return;
    const patch: Partial<Lead> = {};
    const trimmedName = infoName.trim();
    if (trimmedName && trimmedName !== liveLead?.name) patch.name = trimmedName;
    if (infoPhone !== (liveLead?.phone ?? '')) patch.phone = infoPhone;
    if (infoEmail !== (liveLead?.email ?? '')) patch.email = infoEmail;
    if (!Object.keys(patch).length) { setMode('call'); return; }
    setSavingInfo(true);
    setInfoError(null);
    try {
      await updateLead(activeLead.id, patch);
      setLiveLead((prev) => (prev ? { ...prev, ...patch } : prev));
      setMode('call');
    } catch {
      setInfoError('Speichern fehlgeschlagen · nochmal versuchen.');
    } finally {
      setSavingInfo(false);
    }
  }, [activeLead, infoName, infoPhone, infoEmail, liveLead, updateLead]);

  // --- Keyboard-Steuerung ---
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement | null)?.tagName;
      const typing = tag === 'TEXTAREA' || tag === 'INPUT';

      if (mode === 'notes' || mode === 'info') {
        if (e.key === 'Escape') { e.preventDefault(); setMode('call'); }
        return; // Tippen fliesst in die Felder
      }

      if (mode === 'confirm') {
        if (e.key === 'Escape') { e.preventDefault(); setMode('call'); return; }
        if (typing) return;   // im datetime-Feld darf getippt werden
        if (e.key === 'Enter') { e.preventDefault(); endCall(); return; }
        if (e.key.toLowerCase() === 'x') { e.preventDefault(); quickNoAnswer(); return; }
        // Ziffern wählen die Wiedervorlage. Hier sind sie frei — im Abschluss-Panel gehören
        // 1-4 den Emotionen, dort geht die Wahl nur per Klick.
        if (/^[1-9]$/.test(e.key) && presets[+e.key - 1]) { e.preventDefault(); waehlePreset(+e.key - 1); return; }
        return;
      }

      if (mode === 'shortcall') {
        if (e.key === 'Escape') { e.preventDefault(); setMode('call'); return; }
        if (typing) return;
        if (e.key === 'Enter') { e.preventDefault(); setMode('save'); return; } // trotzdem tracken
        if (e.key.toLowerCase() === 'x') { e.preventDefault(); quickNoAnswer(); return; }
        if (/^[1-9]$/.test(e.key) && presets[+e.key - 1]) { e.preventDefault(); waehlePreset(+e.key - 1); return; }
        return;
      }

      if (mode === 'save') {
        if (e.key === 'Escape') { e.preventDefault(); setMode('call'); return; }
        if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') { e.preventDefault(); save(); return; }
        if (typing) return;
        const k = e.key.toLowerCase();
        if (OUTCOME_KEY[k]) { e.preventDefault(); setOutcome((o) => (o === OUTCOME_KEY[k] ? null : OUTCOME_KEY[k])); return; }
        if (['1', '2', '3', '4'].includes(e.key)) { e.preventDefault(); toggleEmotion(EMOTIONS[+e.key - 1]); return; }
        if (e.key === 'Enter') { e.preventDefault(); save(); return; }
        return;
      }

      // --- call-mode ---
      const key = e.key;
      const lower = key.toLowerCase();

      // Ziffer 1–9 → direkt zum passenden Einwand dieser Sektion springen (Detour).
      if (key === 'ArrowDown') { e.preventDefault(); startScroll(SCROLL_SPEED); return; }
      if (key === 'ArrowUp') { e.preventDefault(); startScroll(-SCROLL_SPEED); return; }
      if (/^[1-9]$/.test(key)) {
        const b = visibleBranches[+key - 1];
        if (b) { e.preventDefault(); if (fullText) setFullText(false); setBranchKey(b); }
        return;
      }

      if (key === 'ArrowRight' || key === ' ' || key === 'Enter') {
        // weiter · aus einer Abzweigung zurück ins Skript, sonst nächste Sektion.
        e.preventDefault();
        if (onBranch) setBranchKey(null);
        else if (idx >= total - 1) goToSave();
        else setMainIdx((i) => Math.min(i + 1, total - 1));
      } else if (key === 'ArrowLeft') {
        e.preventDefault();
        if (onBranch) setBranchKey(null);
        else setMainIdx((i) => Math.max(i - 1, 0));
      } else if (key === 'g') {
        e.preventDefault(); setBranchKey(null); setMainIdx(0);
      } else if (key === 'G') {
        e.preventDefault(); setBranchKey(null); setMainIdx(Math.max(0, total - 1));
      } else if (lower === 'b') {
        e.preventDefault(); setBranchKey(null); setMainIdx(total - 1); // direkt zur Buchungs-Sektion
      } else if (lower === 'n') {
        e.preventDefault(); setMode('notes'); setTimeout(() => notesRef.current?.focus(), 0);
      } else if (lower === 'i') {
        e.preventDefault(); setMode('info');
      } else if (lower === 's') {
        e.preventDefault(); goToSave();
      } else if (lower === 'x') {
        e.preventDefault(); quickNoAnswer(); // nicht rangegangen → sofort speichern & beenden
      } else if (key === 'Escape') {
        e.preventDefault();
        if (onBranch) setBranchKey(null); else setMode('confirm');
      }
    };
    window.addEventListener('keydown', onKey);
    // Pfeil hoch/runter: weich scrollen, solange die Taste gehalten wird (nicht springen).
    const onKeyUp = (e: KeyboardEvent) => { if (e.key === 'ArrowDown' || e.key === 'ArrowUp') stopScroll(); };
    window.addEventListener('keyup', onKeyUp);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('keyup', onKeyUp);
    };
  }, [mode, idx, total, onBranch, visibleBranches, fullText, save, goToSave, quickNoAnswer, toggleEmotion,
      endCall, startScroll, stopScroll, presets, waehlePreset]);

  // follow_up_date trägt seit der Zeitwahl den vollen Zeitstempel (der Server schreibt ihn aus
  // callback_at); alte taggenaue Werte liest labelFor genauso.
  const naechsterRueckruf = labelFor(liveLead?.follow_up_date ?? activeLead?.follow_up_date);

  if (!activeLead) return null;

  return (
    <div className="fixed inset-0 z-[60] bg-bg-primary text-text-main flex flex-col overflow-hidden">
      {/* Ambient-Glow: Fokus-Stimmung, keine Ablenkung */}
      <div
        aria-hidden
        className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 h-[36rem] w-[36rem] rounded-full blur-[130px]"
        style={{ background: 'radial-gradient(closest-side, rgba(6,182,212,0.14), transparent)' }}
      />

      {/* HUD oben */}
      <div className="relative flex items-center justify-between px-6 py-4 border-b border-border-primary">
        <div className="flex items-center gap-3 min-w-0">
          <div className="h-10 w-10 shrink-0 rounded-full bg-accent/12 text-accent grid place-items-center font-semibold text-sm ring-1 ring-accent/20">
            {initials}
          </div>
          <div className="min-w-0">
            <div className="font-semibold truncate leading-tight">{liveLead?.name ?? activeLead.name}</div>
            <div className="text-sm text-text-muted truncate">{liveLead?.company ?? activeLead.company}</div>
            {/* Was beim letzten Anruf verabredet wurde. Nur eine Zeile, kein Klickziel — die
                Warteschlange selbst steht im Telefon-Stapel. */}
            {naechsterRueckruf && (
              <div className="text-xs text-text-muted/70 truncate">nächster Rückruf: {naechsterRueckruf}</div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-4 sm:gap-6">
          <div className="flex items-center gap-2 font-mono text-accent tabular-nums text-[0.95rem]">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full rounded-full bg-accent/60 animate-ping" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-accent" />
            </span>
            {fmt(elapsed)}
          </div>
          <div className="flex items-center gap-2">
            {onBranch && (
              <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-accent/12 text-accent border border-accent/25">
                Einwand
              </span>
            )}
            <div className="font-mono text-sm tabular-nums">
              <span className="text-accent font-semibold">{String(Math.min(idx + 1, total)).padStart(2, '0')}</span>
              <span className="text-text-muted"> / {String(total).padStart(2, '0')}</span>
            </div>
          </div>
          <button
            onClick={() => setMode('confirm')}
            className="p-1.5 rounded-lg text-text-muted hover:text-text-main hover:bg-bg-elevated transition-colors"
            aria-label="Schliessen"
          >
            <X size={20} />
          </button>
          <button
            onClick={() => setFullText((v) => !v)}
            className={`p-1.5 rounded-lg transition-colors ${
              fullText ? 'text-accent bg-accent/12' : 'text-text-muted hover:text-text-main hover:bg-bg-elevated'
            }`}
            aria-label={fullText ? 'Fokus-Ansicht' : 'Ganzes Skript'}
            title={fullText ? 'Fokus (eine Sektion)' : 'Ganzes Skript anzeigen'}
          >
            <AlignLeft size={20} />
          </button>
          <button
            onClick={() => setMode('info')}
            className="p-1.5 rounded-lg text-text-muted hover:text-text-main hover:bg-bg-elevated transition-colors"
            aria-label="Info & Kontakt"
            title="Kontakt & Firmendaten (I)"
          >
            <InfoIcon size={20} />
          </button>
        </div>
      </div>

      {/* Fokus: aktueller Abschnitt, gross & lesbar, vertikal zentriert · oder das ganze Skript am Stück
          (Volltext), das sich per Pfeiltaste weich weiterscrollen lässt. */}
      <div className="relative flex-1 overflow-hidden">
        {/* Skript (scrollt) · beim Buchen unsichtbar, bleibt aber im DOM (Position + Scrollstand bleiben) */}
        <div ref={scrollRef} className={`absolute inset-0 overflow-y-auto ${isBooking ? 'invisible pointer-events-none' : ''}`}>
        {fullText ? (
          <div className="w-full mx-auto px-8 py-12 space-y-12">
            {flow.filter((k) => k !== BOOKING_KEY).map((k) => {
              const sec = data[k] ?? { title: '', body: '' };
              const body = fillPlaceholders(sec.body || '', fillCtx);
              return (
                <section key={k}>
                  <h2 className="text-3xl md:text-4xl font-bold mb-4 leading-tight tracking-tight">
                    {sec.title || <span className="text-text-muted">Ohne Titel</span>}
                  </h2>
                  {body ? (
                    <div className="text-2xl md:text-3xl leading-relaxed whitespace-pre-wrap text-text-main/80">{renderScript(body)}</div>
                  ) : (
                    <div className="text-lg text-text-muted italic">Kein Text · frei sprechen.</div>
                  )}
                </section>
              );
            })}
          </div>
        ) : (
        <div className="min-h-full flex items-center">
          <div className="w-full max-w-3xl mx-auto px-8 py-16">
            <div>
              <div className="flex items-center gap-3 mb-5">
                <span className="h-px w-8 bg-accent" />
                <span className="text-xs font-semibold tracking-[0.2em] uppercase text-accent">
                  {onBranch ? 'Einwand · Abzweigung' : `Sektion ${idx + 1}`}
                </span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-8 leading-[1.1] tracking-tight">
                {cur.title || <span className="text-text-muted">Ohne Titel</span>}
              </h1>
              {curBody ? (
                <div className="text-xl md:text-2xl leading-relaxed whitespace-pre-wrap text-text-main/80">
                  {renderScript(curBody)}
                </div>
              ) : (
                <div className="text-lg text-text-muted italic">Kein Text in dieser Sektion. Frei sprechen.</div>
              )}

              {/* Abzweigung verlassen ODER mögliche Einwände an dieser Position anspringen.
                  Hat die Abzweigung eine nächste Stufe (block_meta.next — „kein Interesse"),
                  steht sie daneben: dieselbe Detour-Mechanik, nur ein Schritt weiter. */}
              {onBranch ? (
                <div className="mt-10 flex flex-wrap items-center gap-2">
                  <button
                    onClick={() => setBranchKey(null)}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-accent text-white text-sm font-medium hover:bg-accent/90 transition-colors"
                  >
                    weiter → zurück ins Skript
                  </button>
                  {nextStageKey && (
                    <button
                      onClick={() => setBranchKey(nextStageKey)}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-border-primary bg-bg-surface/60 hover:border-accent/40 hover:bg-accent/[0.04] text-sm text-text-main transition-colors"
                    >
                      Nächste Stufe → <span className="text-text-muted">{branchLabel(data[nextStageKey]?.title || nextStageKey)}</span>
                    </button>
                  )}
                </div>
              ) : (visibleBranches.length > 0 || canSkipOptional) ? (
                <div className="mt-12">
                  {canSkipOptional && (
                    <button
                      onClick={() => { setBranchKey(null); setMainIdx(firstRequiredIdx); }}
                      className="mb-6 inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-border-primary bg-bg-surface/60 hover:border-accent/40 hover:bg-accent/[0.04] text-sm text-text-main transition-colors"
                    >
                      Direkt durchgestellt → <span className="text-text-muted">{data[flow[firstRequiredIdx]]?.title ?? ''}</span>
                    </button>
                  )}
                  {visibleBranches.length > 0 && (
                  <div className="text-[11px] font-semibold uppercase tracking-[0.15em] text-text-muted mb-3">
                    Mögliche Einwände
                  </div>
                  )}
                  <div className="flex flex-wrap gap-2">
                    {visibleBranches.map((k, i) => (
                      <button
                        key={k}
                        onClick={() => setBranchKey(k)}
                        className="group inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-border-primary bg-bg-surface/60 hover:border-accent/40 hover:bg-accent/[0.04] text-sm text-text-main transition-colors"
                      >
                        {i < 9 && (
                          <span className="grid place-items-center h-5 w-5 rounded-md bg-bg-elevated text-[11px] font-mono text-text-muted group-hover:bg-accent/12 group-hover:text-accent">
                            {i + 1}
                          </span>
                        )}
                        {branchLabel(data[k]?.title || k)}
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
        )}
        </div>

        {/* Calendly: bleibt ab dem Cockpit-Start geladen und wird nur ein-/ausgeblendet · beim Closing
            ist „Termin buchen" damit sofort da, ohne Ladezeit mitten im Gespräch. */}
        <div className={`absolute inset-0 overflow-y-auto transition-opacity duration-200 ${
              isBooking ? 'opacity-100 z-10' : 'opacity-0 -z-10 pointer-events-none'}`}>
          <div className="w-full max-w-none px-6 py-3">
            <div className="h-[90vh] rounded-2xl overflow-hidden border border-border-primary bg-bg-surface">
              <iframe src={CALENDLY_URL} title="Termin buchen" className="w-full h-full border-0" />
            </div>
          </div>
        </div>
      </div>

      {/* Fortschritt + Keyboard-Hinweise */}
      <div className="relative px-6 py-4 border-t border-border-primary bg-bg-surface/40 backdrop-blur-sm">
        <div className="flex gap-1.5 mb-3">
          {flow.map((k, i) => (
            <div
              key={k}
              className={`h-1.5 flex-1 rounded-full transition-all duration-300 ${
                i === idx ? 'bg-accent' : i < idx ? 'bg-accent/45' : 'bg-border'
              }`}
            />
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-xs text-text-muted">
          <span><Kbd>←</Kbd> <Kbd>→</Kbd> blättern</span>
          <span><Kbd>G</Kbd> Anfang · <Kbd>⇧G</Kbd> Ende</span>
          {visibleBranches.length > 0 && (
            <span><Kbd>1</Kbd>–<Kbd>{Math.min(visibleBranches.length, 9)}</Kbd> Einwand</span>
          )}
          <span><Kbd>B</Kbd> Termin</span>
          <span><Kbd>N</Kbd> Notiz{notes ? <span className="text-accent"> •</span> : ''}</span>
          <span><Kbd>I</Kbd> Info</span>
          <span><Kbd>S</Kbd> Abschluss</span>
          <span><Kbd>X</Kbd> Nicht erreicht</span>
          <span><Kbd>Esc</Kbd> beenden</span>
          {outcome ? (
            <span className="ml-auto inline-flex items-center gap-1.5 text-text-main">
              <span className="h-1.5 w-1.5 rounded-full bg-accent" /> {outcome}
            </span>
          ) : onBranch ? (
            <span className="ml-auto text-text-muted">
              <Kbd>→</Kbd>&nbsp;zurück ins Skript
            </span>
          ) : nextTitle ? (
            <span className="ml-auto text-text-muted truncate max-w-[45%]">
              Weiter&nbsp;→&nbsp;<span className="text-text-main/70">{nextTitle}</span>
            </span>
          ) : null}
        </div>
      </div>

      {/* Bestätigung: Anruf beenden. Vorher fehlte hier jeder Weg, ein Ergebnis zu loggen — X/Esc
          landete direkt im Verwerfen-Dialog, ohne "niemand rangegangen" oder die Outcome/Notiz-Auswahl
          aus dem Abschluss-Panel. Jetzt dieselben zwei Wege wie im Kurzanruf-Dialog. */}
      {mode === 'confirm' && (
        <Overlay onClose={() => setMode('call')}>
          <h2 className="text-lg font-bold mb-1">Anruf beenden?</h2>
          <p className="text-sm text-text-muted mb-4">Wähle, wie der Anruf gewertet wird, oder verwirf ihn ganz.</p>
          <CallbackPicker
            presets={presets} presetIdx={presetIdx} callbackAt={callbackAt} exactOpen={exactOpen}
            hotkeys kannSpeichern
            onPreset={waehlePreset} onExact={waehleGenau}
            onToggleExact={() => setExactOpen((v) => !v)} onSavePreset={merkeAlsVorschlag}
          />
          <div className="space-y-2">
            <button
              onClick={quickNoAnswer}
              disabled={saving}
              className="w-full py-3 rounded-xl bg-slate-500 text-white text-sm font-semibold hover:bg-slate-600 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              <PhoneOff size={15} /> Niemand rangegangen <span className="opacity-70 text-xs">X</span>
            </button>
            <button
              onClick={goToSave}
              className="w-full py-3 rounded-xl bg-accent text-white text-sm font-semibold hover:bg-accent/90"
            >
              Als Call tracken (Outcome, Notiz …)
            </button>
            <div className="flex items-center gap-3 pt-1">
              <button
                onClick={() => setMode('call')}
                className="flex-1 py-2.5 rounded-xl border border-border-primary text-sm font-semibold text-text-muted hover:text-text-main hover:bg-bg-elevated transition-colors"
              >
                Zurück zum Call <span className="opacity-60 text-xs">Esc</span>
              </button>
              <button
                onClick={endCall}
                className="flex-1 py-2.5 rounded-xl border border-danger/50 text-danger text-sm font-semibold hover:bg-danger/10 transition-colors"
              >
                Verwerfen (nicht tracken) <span className="opacity-70 text-xs">↵</span>
              </button>
            </div>
          </div>
        </Overlay>
      )}

      {/* Kurzer Call: fragen, ob überhaupt getrackt werden soll */}
      {mode === 'shortcall' && (
        <Overlay onClose={() => setMode('call')}>
          <h2 className="text-lg font-bold mb-1">Sehr kurzer Call · {fmt(elapsed)}</h2>
          <p className="text-sm text-text-muted mb-4">Hat jemand abgenommen? Wähle, wie der Anruf gewertet wird.</p>
          <CallbackPicker
            presets={presets} presetIdx={presetIdx} callbackAt={callbackAt} exactOpen={exactOpen}
            hotkeys kannSpeichern
            onPreset={waehlePreset} onExact={waehleGenau}
            onToggleExact={() => setExactOpen((v) => !v)} onSavePreset={merkeAlsVorschlag}
          />
          <div className="space-y-2">
            <button
              onClick={quickNoAnswer}
              disabled={saving}
              className="w-full py-3 rounded-xl bg-slate-500 text-white text-sm font-semibold hover:bg-slate-600 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              <PhoneOff size={15} /> Nicht rangegangen <span className="opacity-70 text-xs">X</span>
            </button>
            <button
              onClick={() => setMode('save')}
              className="w-full py-3 rounded-xl bg-accent text-white text-sm font-semibold hover:bg-accent/90"
            >
              Trotzdem als Call tracken <span className="opacity-70 text-xs">↵</span>
            </button>
            <button
              onClick={endCall}
              className="w-full py-2.5 rounded-xl border border-border-primary text-text-muted text-sm hover:text-text-main transition-colors"
            >
              Verwerfen (nicht tracken) <span className="opacity-60 text-xs">Esc</span>
            </button>
          </div>
        </Overlay>
      )}

      {/* Notiz-Overlay */}
      {mode === 'notes' && (
        <Overlay onClose={() => setMode('call')}>
          <div className="text-xs uppercase tracking-wider text-text-muted mb-2">Notiz · <Kbd>Esc</Kbd> schliesst</div>
          <textarea
            ref={notesRef}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={6}
            placeholder="Notizen zum Gespräch..."
            className="w-full bg-bg-elevated border border-border-primary rounded-lg px-4 py-3 text-base text-text-main focus:outline-none focus:border-accent resize-none"
          />
        </Overlay>
      )}

      {/* Abschluss-Panel */}
      {mode === 'save' && (
        <Overlay onClose={() => setMode('call')}>
          <div className="flex items-center gap-3 mb-5">
            <div className="h-10 w-10 shrink-0 rounded-full bg-accent/12 text-accent grid place-items-center font-semibold text-sm ring-1 ring-accent/20">
              {initials}
            </div>
            <div className="min-w-0">
              <h2 className="text-lg font-bold leading-tight">Anruf abschließen</h2>
              <p className="text-sm text-text-muted">
                <span className="font-mono tabular-nums text-accent">{fmt(elapsed)}</span> · {liveLead?.name ?? activeLead.name}
              </p>
            </div>
          </div>

          <Label>Outcome</Label>
          <div className="flex flex-wrap gap-2 mb-5">
            {OUTCOMES.map((o) => {
              const selected = outcome === o;
              // "Nicht erreicht" ist kein positives Ergebnis → neutral (Slate) statt Accent, wenn gewählt.
              const selectedCls = o === 'NICHT ERREICHT'
                ? 'bg-slate-500 border-slate-500 text-white'
                : 'bg-accent border-accent text-white';
              return (
                <button
                  key={o}
                  onClick={() => setOutcome(selected ? null : o)}
                  className={`flex-1 min-w-[7rem] py-2.5 text-sm font-semibold rounded-lg border transition-colors ${
                    selected ? selectedCls : 'bg-bg-elevated border-border-primary text-text-muted hover:text-text-main'
                  }`}
                >
                  {OUTCOME_LABEL[o]} <span className="opacity-60 text-xs">{OUTCOME_HOTKEY[o]}</span>
                </button>
              );
            })}
          </div>

          {/* Bei WON/LOST wäre ein nächster Anruf Lärm — sonst immer sichtbar, auch bei „Nicht
              erreicht" und bevor ein Outcome gewählt ist. Ziffern gehören hier den Emotionen. */}
          {outcome !== 'WON' && outcome !== 'LOST' && (
            <CallbackPicker
              presets={presets} presetIdx={presetIdx} callbackAt={callbackAt} exactOpen={exactOpen}
              hotkeys={false} kannSpeichern
              onPreset={waehlePreset} onExact={waehleGenau}
              onToggleExact={() => setExactOpen((v) => !v)} onSavePreset={merkeAlsVorschlag}
            />
          )}

          <Label>Emotion</Label>
          <div className="flex flex-wrap gap-2 mb-5">
            {EMOTIONS.map((e, i) => (
              <button
                key={e}
                onClick={() => toggleEmotion(e)}
                className={`px-3 py-2 text-sm rounded-lg border transition-colors ${
                  emotions.includes(e) ? 'bg-accent/15 border-accent text-accent' : 'bg-bg-elevated border-border-primary text-text-muted hover:text-text-main'
                }`}
              >
                {EMOTION_LABEL[e]} <span className="opacity-60 text-xs">{i + 1}</span>
              </button>
            ))}
          </div>

          <Label>Notizen</Label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            placeholder="Notizen zum Gespräch..."
            className="w-full bg-bg-elevated border border-border-primary rounded-lg px-3 py-2 text-sm text-text-main focus:outline-none focus:border-accent resize-none mb-5"
          />

          <div className="flex items-center gap-3">
            <button
              onClick={() => save()}
              disabled={saving}
              className="flex-1 py-3 bg-accent text-white rounded-xl font-semibold text-sm hover:bg-accent/90 disabled:opacity-50"
            >
              {saving ? 'Speichern...' : 'Call speichern'} <span className="opacity-70 text-xs">↵</span>
            </button>
            <button onClick={() => setMode('call')} className="px-4 py-3 text-sm text-text-muted hover:text-text-main">
              Zurück <span className="opacity-60 text-xs">Esc</span>
            </button>
          </div>
        </Overlay>
      )}

      {/* Info-Panel: Kontakt & Firmendaten kurz nachschauen oder korrigieren, ohne den Call zu verlassen */}
      {mode === 'info' && liveLead && (
        <Overlay onClose={() => setMode('call')}>
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-lg font-bold leading-tight">Kontakt & Details</h2>
            <span className="text-sm text-text-muted truncate max-w-[50%]">{liveLead.company}</span>
          </div>

          <Label>Ansprechpartner</Label>
          <input
            type="text"
            value={infoName}
            onChange={(e) => setInfoName(e.target.value)}
            placeholder="Name"
            className="w-full bg-bg-elevated border border-border-primary rounded-lg px-3 py-2 text-sm text-text-main focus:outline-none focus:border-accent mb-3"
          />
          <input
            type="tel"
            value={infoPhone}
            onChange={(e) => setInfoPhone(e.target.value)}
            placeholder="Telefonnummer"
            className="w-full bg-bg-elevated border border-border-primary rounded-lg px-3 py-2 text-sm text-text-main focus:outline-none focus:border-accent mb-3"
          />
          <input
            type="email"
            value={infoEmail}
            onChange={(e) => setInfoEmail(e.target.value)}
            placeholder="E-Mail"
            className="w-full bg-bg-elevated border border-border-primary rounded-lg px-3 py-2 text-sm text-text-main focus:outline-none focus:border-accent mb-5"
          />

          <Label>Firma</Label>
          <div className="space-y-1.5 text-sm mb-5">
            {liveLead.website && (
              <div>
                <a href={liveLead.website} target="_blank" rel="noopener noreferrer" className="text-accent hover:underline break-all">
                  {liveLead.website}
                </a>
              </div>
            )}
            <div className="flex items-center gap-2 flex-wrap">
              <span className="px-2 py-0.5 text-xs rounded bg-bg-elevated border border-border-primary text-text-muted">
                {liveLead.source}
              </span>
              {liveLead.niche && (
                <span className="px-2 py-0.5 text-xs rounded bg-accent/10 text-accent border border-accent/30">{liveLead.niche}</span>
              )}
            </div>
            {liveLead.script_context?.job_title && (
              <div className="pt-1"><span className="text-text-muted text-xs">Jobtitel: </span>{liveLead.script_context.job_title}</div>
            )}
            {liveLead.script_context?.recoverable_eur != null && liveLead.script_context.recoverable_eur > 0 && (
              <div><span className="text-text-muted text-xs">Rückholbar (Schätzung): </span>~{liveLead.script_context.recoverable_eur.toLocaleString('de-DE')} €/Jahr</div>
            )}
          </div>

          {calls.filter((c) => c.lead_id === activeLead.id).length > 0 && (
            <>
              <Label>Letzte Calls</Label>
              <div className="space-y-2 max-h-40 overflow-y-auto pr-1 mb-5">
                {calls.filter((c) => c.lead_id === activeLead.id).slice(0, 3).map((c, i) => (
                  <div key={c.id ?? i} className="border-l-2 border-accent/30 pl-3 text-sm">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-text-main">{c.outcome ?? '-'}</span>
                      <span className="text-text-muted text-xs">
                        {new Date(c.date).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                    {c.notes && <div className="text-text-muted italic mt-0.5">"{c.notes}"</div>}
                  </div>
                ))}
              </div>
            </>
          )}

          {infoError && <p className="text-sm text-danger mb-3">{infoError}</p>}

          <div className="flex items-center gap-3">
            <button
              onClick={saveInfo}
              disabled={savingInfo}
              className="flex-1 py-3 bg-accent text-white rounded-xl font-semibold text-sm hover:bg-accent/90 disabled:opacity-50"
            >
              {savingInfo ? 'Speichern...' : 'Speichern'}
            </button>
            <button onClick={() => setMode('call')} className="px-4 py-3 text-sm text-text-muted hover:text-text-main">
              Zurück <span className="opacity-60 text-xs">Esc</span>
            </button>
          </div>
        </Overlay>
      )}
    </div>
  );
}

/** „Wann wieder anrufen?" — Vorschlags-Chips plus aufklappbare Feinwahl.
 *
 *  Steht in allen drei Abschluss-Dialogen. Ein Vorschlag ist beim Öffnen vorgewählt, damit
 *  „Niemand rangegangen" ein einziger Tastendruck bleibt; nochmal antippen wählt ab, dann greift
 *  die Auto-Kadenz aus den Einstellungen. Ziffern-Kürzel gibt es nur dort, wo sie frei sind
 *  (confirm/shortcall) — im Abschluss-Panel gehören 1-4 den Emotionen. */
function CallbackPicker({
  presets, presetIdx, callbackAt, exactOpen, hotkeys, kannSpeichern,
  onPreset, onExact, onToggleExact, onSavePreset,
}: {
  presets: CallbackPreset[];
  presetIdx: number | null;
  callbackAt: string | null;
  exactOpen: boolean;
  hotkeys: boolean;
  kannSpeichern: boolean;
  onPreset: (i: number) => void;
  onExact: (iso: string | null) => void;
  onToggleExact: () => void;
  onSavePreset: () => void;
}) {
  const eigeneZeit = callbackAt != null && presetIdx == null;
  return (
    <div className="mb-4 rounded-xl border border-border-primary bg-bg-elevated/40 p-3">
      <div className="flex items-baseline justify-between mb-2">
        <div className="text-xs uppercase tracking-wider text-text-muted">Wieder anrufen</div>
        <div className="text-xs text-text-muted tabular-nums">
          {labelFor(callbackAt) ?? 'nach Rhythmus aus den Einstellungen'}
        </div>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {presets.map((p, i) => (
          <button
            key={`${p.label}-${i}`}
            onClick={() => onPreset(i)}
            className={`px-2.5 py-1.5 text-sm rounded-lg border transition-colors ${
              presetIdx === i
                ? 'bg-accent/15 border-accent text-accent'
                : 'bg-bg-elevated border-border-primary text-text-muted hover:text-text-main'
            }`}
          >
            {p.label}
            {hotkeys && i < 9 && <span className="opacity-50 text-xs ml-1">{i + 1}</span>}
          </button>
        ))}
        <button
          onClick={onToggleExact}
          className={`px-2.5 py-1.5 text-sm rounded-lg border transition-colors ${
            eigeneZeit
              ? 'bg-accent/15 border-accent text-accent'
              : 'bg-bg-elevated border-border-primary text-text-muted hover:text-text-main'
          }`}
        >
          {exactOpen ? '▾' : '▸'} genauer Zeitpunkt
        </button>
      </div>
      {exactOpen && (
        <div className="flex items-center gap-2 mt-2">
          <input
            type="datetime-local"
            value={callbackAt ? isoToLocalInput(callbackAt) : ''}
            onChange={(e) => onExact(localInputToIso(e.target.value))}
            className="flex-1 bg-bg-elevated border border-border-primary rounded-lg px-3 py-2 text-sm text-text-main focus:outline-none focus:border-accent"
          />
          {kannSpeichern && eigeneZeit && (
            <button
              onClick={onSavePreset}
              title="Diesen Abstand als Vorschlag merken — für alle sichtbar"
              className="px-3 py-2 text-sm rounded-lg border border-border-primary text-text-muted hover:text-text-main whitespace-nowrap"
            >
              + speichern
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// --- kleine UI-Helfer ---
function Kbd({ children }: { children: React.ReactNode }) {
  return <kbd className="inline-block min-w-[1.5em] text-center px-1.5 py-0.5 rounded-md border border-border-primary bg-bg-elevated text-[0.7rem] font-mono text-text-main shadow-[0_1px_0_rgba(0,0,0,0.06)]">{children}</kbd>;
}
function Label({ children }: { children: React.ReactNode }) {
  return <div className="text-xs uppercase tracking-wider text-text-muted mb-2">{children}</div>;
}
function Overlay({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  return (
    <div
      className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm flex items-end md:items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg bg-bg-surface/95 backdrop-blur-xl border border-border-primary rounded-2xl p-6 ring-1 ring-white/5"
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}
